import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Chip, Collapse, IconButton, Stack, Typography, Button } from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { AnnouncementRow } from '@rhwb/types';
import { fetchAnnouncements, markAnnouncementRead, acknowledgeAnnouncement } from '../lib/announcements';

/**
 * Announcements from RHWB management, on the runner dashboard.
 *
 * This renders NOTHING today, and that is the intended state. Phase-1
 * announcements target coaches (audience_kind = 'all_coaches'), so the API's
 * visibility predicate returns an empty feed for a runner and this component
 * returns null. It exists now so that turning runners on in phase 2 is a
 * publishing decision rather than a frontend project — which is also why it
 * fails silently: a runner must never see an error for a feature that has not
 * been announced to them.
 */
export default function AnnouncementsBanner() {
  const [items, setItems] = useState<AnnouncementRow[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    fetchAnnouncements()
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      // Silent by design — see the component comment.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = items.filter((item) => !dismissed[item.id]);
  if (visible.length === 0) return null;

  const dismiss = (item: AnnouncementRow) => {
    setDismissed((prev) => ({ ...prev, [item.id]: true }));
    markAnnouncementRead(item.id).catch(() => {});
  };

  const acknowledge = (item: AnnouncementRow) => {
    setItems((prev) =>
      prev.map((row) => (row.id === item.id ? { ...row, acknowledged_at: new Date().toISOString() } : row))
    );
    acknowledgeAnnouncement(item.id).catch(() => {});
  };

  return (
    <Stack spacing={1.5} sx={{ mb: 2 }}>
      {visible.map((item) => {
        const isOpen = expanded[item.id] ?? false;
        return (
          <Card key={item.id} variant="outlined" sx={{ borderColor: 'primary.light' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <CampaignIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {item.title}
                    </Typography>
                    {item.pinned && <Chip size="small" label="Pinned" color="warning" variant="outlined" />}
                  </Stack>
                  <Collapse in={isOpen} collapsedSize={0}>
                    {/*
                      Plain text. announcements.body is stored as plain text and
                      is never rendered as markup anywhere in this monorepo;
                      pre-wrap keeps the author's line breaks without
                      interpreting anything.
                    */}
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1, wordBreak: 'break-word' }}>
                      {item.body}
                    </Typography>
                    {item.requires_ack && !item.acknowledged_at && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<CheckCircleOutlineIcon />}
                        sx={{ mt: 1.5 }}
                        onClick={() => acknowledge(item)}
                      >
                        Got it
                      </Button>
                    )}
                    {item.acknowledged_at && (
                      <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1.5 }}>
                        Acknowledged
                      </Typography>
                    )}
                  </Collapse>
                </Box>
                <IconButton
                  size="small"
                  aria-label={isOpen ? 'Collapse announcement' : 'Expand announcement'}
                  onClick={() => setExpanded((prev) => ({ ...prev, [item.id]: !isOpen }))}
                >
                  {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small" aria-label="Dismiss announcement" onClick={() => dismiss(item)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}
